import { uniqBy } from 'lodash';
import type Luminary from '@lumen5/luminary';
import { type TimeAwareMediaAsset, VideoData } from '@lumen5/luminary';

import {
    ApiService,
    type AssetSourceItemRequest,
    type AssetTypes,
    SourceEnum,
    type VideoAssetModel,
} from 'js/src/client';
import { startAudioEnhancementOperation } from 'js/src/data/audio-enhancement';
import { slideBackgroundModelIsVideoBackgroundModel } from 'js/src/data/media';
import { assetContainerModelIsBackground } from 'js/src/data/uploads';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { updateSceneGroupDurationToEqualVoiceDrivenAssetDuration } from 'js/src/luminary/voicedriven-scenegroup-utils';

export const updateVideoAssetUrlsOperation = createLuminaryOperation(async ({ luminary, call }) => {
    const assetItems = getAssetsNotTranscoded(luminary);
    if (assetItems.length === 0) {
        return;
    }
    try {
        const sourceDataMap = await fetchVideoAssetsFromBackend(assetItems);
        const assetsUpdated = call(
            setBackgroundTranscodedMediaAssetsOperation,
            assetItems,
            sourceDataMap,
        );

        // kick off audio enhancement for any talking head media assets (only for paid users)
        if (SERVER.USER.plan_price > 0) {
            assetsUpdated.forEach((updatedAsset) => {
                const isTalkingHeadMediaAsset = VideoData.isSceneGroup(updatedAsset.container);
                if (isTalkingHeadMediaAsset) {
                    const sceneGroupId = updatedAsset.container.id;
                    call(startAudioEnhancementOperation, sceneGroupId);
                }
            });
        }
    } catch (error) {
        ErrorReporter.capture(error);
    }
});

export function deduplicateByUuid<T extends { asset_uuid: string }>(items: T[]): T[] {
    return uniqBy(items, 'asset_uuid');
}

export function getAssetsNotTranscoded(luminary: Luminary) {
    // Check all assets in the video to see if they need to be updated
    const assetsToUpdate = luminary
        .getAssetsByType(VideoData.MEDIA_ASSET_TYPE)
        .filter((asset) => videoAssetIsNotTranscoded(asset));
    if (assetsToUpdate.length === 0) {
        return [];
    }

    // The same media source can be used for multiple assets, so dedupe first
    const items = assetsToUpdate.map((asset) => {
        return {
            asset_type: asset.sourceData.type as AssetTypes,
            asset_uuid: asset.sourceData.uuid,
        };
    });
    const asset_items = deduplicateByUuid(items);
    return asset_items;
}

/**
 * @returns a Record of asset UUIDs to their corresponding VideoAssetModel
 */
export async function fetchVideoAssetsFromBackend(asset_items: AssetSourceItemRequest[]) {
    const response = await ApiService.bulkAssetSourceCreate({
        body: { asset_items },
    });
    const sourceDataMap: Record<string, VideoAssetModel> = response.data.reduce(
        (acc, assetData) => {
            if (!assetContainerModelIsBackground(assetData)) {
                return acc;
            }
            const backgroundAsset = assetData[assetData.type];
            if (!slideBackgroundModelIsVideoBackgroundModel(backgroundAsset)) {
                return acc;
            }
            const asset = backgroundAsset[backgroundAsset.type];
            if (!asset.uuid) {
                return acc;
            }
            return {
                ...acc,
                [asset.uuid]: asset,
            };
        },
        {},
    );
    return sourceDataMap;
}

export const setBackgroundTranscodedMediaAssetsOperation = createLuminaryOperation(
    (
        { luminary },
        asset_items: AssetSourceItemRequest[],
        sourceDataMap: Record<string, VideoAssetModel>,
    ) => {
        const allMediaAssets = luminary.getAssetsByType(VideoData.MEDIA_ASSET_TYPE);
        const assetsUpdated: TimeAwareMediaAsset[] = [];
        asset_items.forEach((assetItem) => {
            allMediaAssets
                .filter((asset) => asset.linkToSceneGroup !== true) // the sourceData for assets linked to the sceneGroup should be set in the sceneGroup.mediaAsset, not the one(s) on the scene level
                .filter((asset) => asset.sourceData.uuid === assetItem.asset_uuid)
                .forEach((assetToUpdate) => {
                    const sourceData = sourceDataMap[assetToUpdate.sourceData.uuid];
                    if (!sourceData || !sourceData.render_url) {
                        // background transcode not finished yet
                        return;
                    }
                    if (assetToUpdate.sourceData.render_url === sourceData.render_url) {
                        // already updated
                        return;
                    }
                    assetToUpdate.setSourceDataWithoutResettingProperties({
                        ...assetToUpdate.sourceData,
                        render_url: sourceData.render_url,
                        preview_url: sourceData.preview_url,
                        thumbnail_url: sourceData.thumbnail_url,
                    });
                    assetsUpdated.push(assetToUpdate);
                    const luminaryParent = assetToUpdate._luminaryParent;
                    if (VideoData.isSceneGroup(luminaryParent)) {
                        void updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({
                            sceneGroup: luminaryParent,
                            waitForLuminary: true,
                        });
                    }
                });
        });
        return assetsUpdated;
    },
);

export function videoAssetIsNotTranscoded(mediaAsset: TimeAwareMediaAsset): boolean {
    return (
        (mediaAsset.isVideo as boolean) &&
        mediaAsset.sourceData.source === SourceEnum.SOURCE_USER &&
        mediaAsset.renderUrl === null
    );
}
